import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { GameHeader } from "../WordyVerse/Components/GameBoard/GameHeader";
import ZoomIn from "@mui/icons-material/ZoomIn";
import Settings from "@mui/icons-material/Settings";
import Undo from "@mui/icons-material/Undo";
import Redo from "@mui/icons-material/Redo";
import Facebook from "@mui/icons-material/Facebook";
import Twitter from "@mui/icons-material/Twitter";
import {
  GRID_SIZE,
  PIPE_TYPES,
  PIPE_CONNECTIONS,
  COLORS,
} from "./constants/pipes";
import {
  generatePuzzle,
  getRandomPipeType,
  getRandomRotation,
  generateRandomEdgePosition,
  getOppositeEdgePosition,
  generateValidPath,
  getEndpointPipeType,
  getPipeTypeForPath,
} from "./utils/puzzleGenerator";
import {
  checkSolution,
  getConnectionsForRotation,
  arePipesConnected,
  findStartAndEndPoints,
} from "./utils/solutionChecker";
import PipeSegment from "./components/PipeSegment";

// Animation keyframes
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(90deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Add the keyframes for clockwise rotation
const rotateClockwise = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(90deg);
  }
`;

// Styled components
const GameContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(2),
  flex: 1,
  width: "100%",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#FFFFFF",
}));

const GameCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  margin: "0 auto",
  overflow: "visible",
  position: "relative",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 0 20px rgba(0,0,0,0.1)",
  borderRadius: theme.shape.borderRadius * 2,
  [theme.breakpoints.up("md")]: {
    maxWidth: 550,
  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${GRID_SIZE}, 80px)`,
  gap: 8,
  justifyContent: "center",
  margin: "0 auto",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    gap: 12,
  },
}));

// Add new styled components for endpoints
const EndpointIndicator = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  backgroundColor: "#FF6B6B",
  boxShadow: "0 0 8px rgba(255, 107, 107, 0.5)",
  zIndex: 1,
}));

const EndpointContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    width: "100%",
    height: "100%",
    border: "2px solid #FF6B6B",
    borderRadius: "4px",
    animation: `${pulse} 2s infinite`,
  },
}));

const getPipeImage = (type) => {
  switch (type) {
    case PIPE_TYPES.CROSS:
      return "/assets/press-pipes/cross.svg";
    case PIPE_TYPES.ELBOW_BOTTOM_LEFT:
      return "/assets/press-pipes/elbow-bottom-left.svg";
    case PIPE_TYPES.ELBOW_BOTTOM_RIGHT:
      return "/assets/press-pipes/elbow-bottom-right.svg";
    case PIPE_TYPES.ELBOW_LEFT_TOP:
      return "/assets/press-pipes/elbow-left-top.svg";
    case PIPE_TYPES.ELBOW_TOP_RIGHT:
      return "/assets/press-pipes/elbow-top-right.svg";
    case PIPE_TYPES.STRAIGHT_HORIZONTAL:
      return "/assets/press-pipes/straight-horizontal.svg";
    case PIPE_TYPES.STRAIGHT_VERTICAL:
      return "/assets/press-pipes/straight-vertical.svg";
    case PIPE_TYPES.T_DOWN:
      return "/assets/press-pipes/t-down.svg";
    case PIPE_TYPES.T_LEFT:
      return "/assets/press-pipes/t-left.svg";
    case PIPE_TYPES.T_RIGHT:
      return "/assets/press-pipes/t-right.svg";
    case PIPE_TYPES.T_UP:
      return "/assets/press-pipes/t-up.svg";
    case PIPE_TYPES.END_BOTTOM:
      return "/assets/press-pipes/end-bottom.svg";
    case PIPE_TYPES.END_LEFT:
      return "/assets/press-pipes/end-left.svg";
    case PIPE_TYPES.END_RIGHT:
      return "/assets/press-pipes/end-right.svg";
    case PIPE_TYPES.END_TOP:
      return "/assets/press-pipes/end-top.svg";
    default:
      return "/assets/press-pipes/straight-horizontal.svg"; // Default fallback
  }
};

// Helper function to get valid neighbors for BFS
const getValidNeighbors = (pos, visited) => {
  const neighbors = [];
  const directions = [
    { dx: 0, dy: -1, dir: "top" }, // top
    { dx: 1, dy: 0, dir: "right" }, // right
    { dx: 0, dy: 1, dir: "bottom" }, // bottom
    { dx: -1, dy: 0, dir: "left" }, // left
  ];

  for (const { dx, dy, dir } of directions) {
    const newX = pos.x + dx;
    const newY = pos.y + dy;

    if (
      newX >= 0 &&
      newX < GRID_SIZE &&
      newY >= 0 &&
      newY < GRID_SIZE &&
      !visited.has(`${newX},${newY}`)
    ) {
      neighbors.push({ x: newX, y: newY, dir });
    }
  }

  return neighbors;
};

// Helper function to find path using BFS
const findPath = (start, end) => {
  const queue = [{ pos: start, path: [start], dirs: [] }];
  const visited = new Set([`${start.x},${start.y}`]);

  while (queue.length > 0) {
    const { pos, path, dirs } = queue.shift();

    if (pos.x === end.x && pos.y === end.y) {
      return { path, dirs };
    }

    const neighbors = getValidNeighbors(pos, visited);
    for (const neighbor of neighbors) {
      const key = `${neighbor.x},${neighbor.y}`;
      if (!visited.has(key)) {
        visited.add(key);
        const newPath = [...path, { x: neighbor.x, y: neighbor.y }];
        const newDirs = [...dirs, neighbor.dir];
        queue.push({ pos: neighbor, path: newPath, dirs: newDirs });
      }
    }
  }

  return null;
};

const BasePressPipes = () => {
  const [grid, setGrid] = useState([]);
  const [isSolved, setIsSolved] = useState(false);

  useEffect(() => {
    initializeGrid();
  }, []);

  const initializeGrid = () => {
    const newGrid = generatePuzzle();
    setGrid(newGrid);
    setIsSolved(false);
  };

  const handleTileClick = (row, col) => {
    if (grid[row][col].isLocked) return;

    setGrid((prevGrid) => {
      const newGrid = prevGrid.map((r) => [...r]);
      newGrid[row][col] = {
        ...newGrid[row][col],
        rotation: (newGrid[row][col].rotation + 90) % 360,
      };

      // Check if the puzzle is solved after each move
      if (checkSolution(newGrid)) {
        setIsSolved(true);
      }

      return newGrid;
    });
  };

  return (
    <GameContainer>
      <GameCard>
        <CardContent>
          <GameHeader
            bannerPath="/assets/customer-logos/banner/comet-chronicles.svg"
            title="Print Press Pipes"
          />

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Press Pipes
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${GRID_SIZE}, 80px)`,
                gap: 0,
                justifyContent: "center",
                margin: "0 auto",
                border: "1px solid #CCCCCC",
                backgroundColor: "#FFFFFF",
              }}
            >
              {grid.map((row, rowIndex) =>
                row.map((tile, colIndex) => (
                  <Box
                    key={`${rowIndex}-${colIndex}`}
                    onClick={() => handleTileClick(rowIndex, colIndex)}
                    sx={{
                      width: "80px",
                      height: "80px",
                      cursor: tile.isLocked ? "not-allowed" : "pointer",
                      border: "1px solid #CCCCCC",
                      padding: 0,
                      overflow: "hidden",
                    }}
                  >
                    <PipeSegment
                      {...tile}
                      row={rowIndex}
                      col={colIndex}
                      getPipeImage={getPipeImage}
                    />
                  </Box>
                ))
              )}
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={initializeGrid}
              sx={{ mt: 2 }}
            >
              New Puzzle
            </Button>

            {isSolved && (
              <Typography
                variant="h5"
                sx={{
                  color: "#4CAF50",
                  mt: 2,
                  animation: `${pulse} 2s infinite`,
                }}
              >
                Puzzle Solved!
              </Typography>
            )}
          </Box>
        </CardContent>
      </GameCard>
    </GameContainer>
  );
};

export default BasePressPipes;
